/**
 * Silent reconnection / retry layer for fetch().
 *
 * Wraps native fetch so transient failures (network drops, 502/503/504,
 * timeouts) are retried silently up to N times with exponential backoff
 * before surfacing the error to the UI. The student never sees a "session
 * dropped" toast just because the school wifi blinked.
 *
 * Usage:
 *   import { fetchWithRetry } from '@/utils/silentRetry';
 *   const r = await fetchWithRetry(url, { credentials: 'include' });
 */

const DEFAULT_MAX_RETRIES = 3;
const DEFAULT_BASE_DELAY_MS = 500;
const TRANSIENT_STATUS = new Set([408, 425, 429, 500, 502, 503, 504]);

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Retry a fetch call silently on transient failures.
 *
 * @param {string|Request} input - URL or Request object.
 * @param {RequestInit} init - Standard fetch init.
 * @param {object} opts
 *   - retries: max retry attempts (default 3)
 *   - baseDelayMs: starting backoff in ms (default 500 → 500/1000/2000)
 *   - onRetry: callback({ attempt, delay, reason }) — useful for telemetry
 */
export async function fetchWithRetry(input, init = {}, opts = {}) {
  const {
    retries = DEFAULT_MAX_RETRIES,
    baseDelayMs = DEFAULT_BASE_DELAY_MS,
    onRetry,
  } = opts;

  let attempt = 0;
  let lastError;

  while (attempt <= retries) {
    try {
      const response = await fetch(input, init);
      // Retry only for transient server errors. Auth / 4xx are not retried.
      if (TRANSIENT_STATUS.has(response.status) && attempt < retries) {
        const delay = baseDelayMs * Math.pow(2, attempt);
        onRetry?.({ attempt: attempt + 1, delay, reason: `HTTP ${response.status}` });
        await sleep(delay);
        attempt += 1;
        continue;
      }
      return response;
    } catch (err) {
      lastError = err;
      if (attempt >= retries) break;
      const delay = baseDelayMs * Math.pow(2, attempt);
      onRetry?.({ attempt: attempt + 1, delay, reason: err?.message || 'network-error' });
      await sleep(delay);
      attempt += 1;
    }
  }

  throw lastError || new Error('fetchWithRetry: exhausted attempts');
}

export default fetchWithRetry;
